import type { FunnelQueryRequest, FunnelStepResult } from "@/api/insights";
import type {
  ListFunnelsResponse,
  PageViewFunnelQueryRequest,
} from "@/api/funnels";
import type { Funnel, FunnelStepConfig } from "@/funnels/types";

import { countStep, computePatternStepCounts } from "../graph/aggregates";
import { getPreviewGraph } from "../graph";
import type { PreviewFunnel } from "../graph/types";
import { PREVIEW_ENV_ID } from "../runtime";

function toApiFunnel(f: PreviewFunnel): Funnel {
  const steps: FunnelStepConfig[] = f.steps.map((s) => ({
    type: "pageView",
    label: s.label,
    urlPatterns: s.urlPatterns,
  }));
  return {
    id: f.id,
    envID: PREVIEW_ENV_ID,
    name: f.name,
    config: { steps },
    createdAt: new Date(f.createdAt).toISOString(),
    updatedAt: new Date(f.updatedAt).toISOString(),
  };
}

export function handleListFunnels(
  _params: Record<string, never>,
): ListFunnelsResponse {
  const graph = getPreviewGraph();
  return { funnels: graph.funnels.map(toApiFunnel) };
}

export function handleAnalyticsFunnelQuery(
  params: FunnelQueryRequest,
): FunnelStepResult[] {
  const graph = getPreviewGraph();
  const stepNames = params.steps.map((s) => s.eventName);

  // Reuse precomputed counts when the requested steps match a known funnel.
  const match = graph.funnels.find(
    (f) =>
      f.steps.length === stepNames.length &&
      f.steps.every((s, i) => s.eventName === stepNames[i]),
  );

  const counts = match
    ? match.stepCounts
    : stepNames.map((_, i) => countStep(graph.sessions, stepNames, i));

  const firstCount = counts[0] ?? 0;
  return counts.map((count, i) => {
    const prev = i > 0 ? counts[i - 1] : firstCount;
    return {
      stepIndex: i,
      stepName: params.steps[i].eventName,
      count,
      conversionRate: firstCount === 0 ? 0 : (count / firstCount) * 100,
      dropoffPct: i === 0 || prev === 0 ? 0 : ((prev - count) / prev) * 100,
    };
  });
}

export function handleFunnelQuery(
  params: PageViewFunnelQueryRequest,
): FunnelStepResult[] {
  const graph = getPreviewGraph();
  const patternSteps = params.steps
    .filter(
      (s): s is Extract<FunnelStepConfig, { type: "pageView" }> =>
        s.type === "pageView",
    )
    .map((s) => ({
      urlPatterns: s.urlPatterns,
    }));

  // Reuse a funnel's precomputed counts if the steps match exactly.
  const match = graph.funnels.find(
    (f) =>
      f.steps.length === patternSteps.length &&
      f.steps.every(
        (s, i) =>
          s.urlPatterns.length === patternSteps[i].urlPatterns.length &&
          s.urlPatterns.every((p, j) => p === patternSteps[i].urlPatterns[j]),
      ),
  );

  const counts = match
    ? match.stepCounts
    : computePatternStepCounts(graph.sessions, patternSteps);

  const firstCount = counts[0] ?? 0;
  return counts.map((count, i) => {
    const prev = i > 0 ? counts[i - 1] : firstCount;
    return {
      stepIndex: i,
      stepName: params.steps[i].label || `Step ${i + 1}`,
      count,
      conversionRate: firstCount === 0 ? 0 : (count / firstCount) * 100,
      dropoffPct: i === 0 || prev === 0 ? 0 : ((prev - count) / prev) * 100,
    };
  });
}
